import { Box, type BoxProps } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';

export type PillTone =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral';

export interface PillProps extends Omit<BoxProps, 'color'> {
  tone?: PillTone;
  /** Emphasise with a filled background (default) or show an outlined variant. */
  filled?: boolean;
  children: ReactNode;
}

const TONE_SX: Record<PillTone, { bg: string; fg: string; border: string }> = {
  primary: { bg: 'primary.main', fg: 'primary.contrastText', border: 'primary.main' },
  secondary: { bg: 'secondary.main', fg: 'secondary.contrastText', border: 'secondary.main' },
  success: { bg: 'success.main', fg: 'success.contrastText', border: 'success.main' },
  warning: { bg: 'warning.main', fg: 'warning.contrastText', border: 'warning.main' },
  error: { bg: 'error.main', fg: 'error.contrastText', border: 'error.main' },
  info: { bg: 'info.main', fg: 'info.contrastText', border: 'info.main' },
  neutral: { bg: 'grey.200', fg: 'text.primary', border: 'grey.400' },
};

/**
 * Small rounded pill for inline metadata such as "new", "beta", or tag labels.
 * Lighter-weight than the {@link StatusChip} and intended for dense lists.
 */
export const Pill = forwardRef<HTMLSpanElement, PillProps>(function Pill(
  { tone = 'neutral', filled = true, children, sx, ...rest },
  ref,
) {
  const palette = TONE_SX[tone];
  return (
    <Box
      ref={ref}
      component="span"
      role="status"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: 999,
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: 0.2,
        textTransform: 'uppercase',
        bgcolor: filled ? palette.bg : 'transparent',
        color: filled ? palette.fg : palette.border,
        border: filled ? 'none' : `1px solid`,
        borderColor: palette.border,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
});
